import { FunctionComponent } from "react";
import { TFunction } from "i18next";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";

import { blacklistBlockLabelKey } from "../utils/blacklistBlockLabelKey";
import { StepFailure } from "../utils/resolveStepFailure";
import { InfoTag } from "./InfoTag";

/** The step name shown in the tag, falling back when the step type is unknown/legacy. */
const resolveStepLabel = (stepType: string | null, t: TFunction): string => {
	const label = stepType ? t(`outbox.${stepType}`) : "";

	// i18next echoes the key back when it is missing — treat that as "no label for this step type".
	return !label || label === `outbox.${stepType}` ? t("outbox.failure.stepFallback") : label;
};

/** What happened, naming the columns a step had no value for when the API knows them (PREC-4440). */
export const stepFailureMessage = (failure: StepFailure, t: TFunction): string =>
	failure.missingColumns?.length
		? t(`outbox.failure.${failure.category}.messageWithColumns`, {
				columns: failure.missingColumns.join(", "),
			})
		: t(`outbox.failure.${failure.category}.message`);

/**
 * The Outbox tag for a step that failed to send. The API only tells us *which* category the
 * failure falls into (the raw provider payload never leaves the server), so all the user-facing
 * wording — what happened and what to do about it — is keyed off that category here.
 */
export const StepFailureTag: FunctionComponent<{ failure: StepFailure; t: TFunction }> = ({
	failure,
	t,
}) => (
	<InfoTag
		icon={faExclamationCircle}
		label={t(`outbox.failure.${failure.category}.label`, {
			step: resolveStepLabel(failure.stepType, t),
		})}
		style={failure.severity}
		tooltip={
			<span className="flex flex-col gap-1">
				<span>{stepFailureMessage(failure, t)}</span>
				{failure.blockedBy && (
					<span>
						{t(blacklistBlockLabelKey(failure.blockedBy.origin), {
							value: failure.blockedBy.value,
						})}
					</span>
				)}
				<span className="opacity-80">{t(`outbox.failure.${failure.category}.actionHint`)}</span>
			</span>
		}
	/>
);
